import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { Worker, TimeRecord, Company } from '../types';
import { workerService } from '../services/workerService';
import { companyService } from '../services/companyService';
import { EditTimeRecordDialog } from './EditTimeRecordDialog';
import { AddDayDialog } from './AddDayDialog';
import { AddBonusDialog } from './AddBonusDialog';
import { ManageBonusesDialog } from './ManageBonusesDialog';
import dayjs from 'dayjs';

interface TimeCalendarProps {
  worker: Worker;
  onClose: () => void;
  userRole?: string;
}

export const TimeCalendar: React.FC<TimeCalendarProps> = ({ worker, onClose, userRole }) => {
  const [timeRecords, setTimeRecords] = useState<TimeRecord[]>([]);
  const [viewType, setViewType] = useState<'daily' | 'weekly'>('daily');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDayDialogOpen, setAddDayDialogOpen] = useState(false);
  const [addBonusDialogOpen, setAddBonusDialogOpen] = useState(false);
  const [manageBonusesDialogOpen, setManageBonusesDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<TimeRecord | null>(null);
  const [company, setCompany] = useState<Company | null>(null);

  useEffect(() => {
    loadTimeRecords();
    loadCompanyInfo();
  }, [worker.id, userRole]);

  const loadTimeRecords = async () => {
    try {
      setLoading(true);
      const data = await workerService.getWorkerTimeRecords(worker.id);
      console.log('Time records data:', data);
      // Depurar resúmenes semanales
      const summaries = data.filter(r => r.is_summary || r.date?.includes('Resumen Semanal'));
      console.log('Weekly summaries from backend:', summaries);
      summaries.forEach(s => {
        console.log(`Summary: ${s.date}, bonus: ${s.bonus}, total_pay: ${s.total_pay}`);
      });
      setTimeRecords(data);
    } catch (error) {
      console.error('Error cargando registros de tiempo:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      // Empresa usa su propia empresa (first_day_of_week y datos); admin/super_admin por worker
      const companyData = userRole === 'empresa'
        ? await companyService.getMyCompany()
        : await companyService.getCompany(worker.company_id);
      setCompany(companyData);
    } catch (error) {
      console.error('Error cargando información de la empresa:', error);
    }
  };

  const handleEditRecord = (record: TimeRecord) => {
    setSelectedRecord(record);
    setEditDialogOpen(true);
  };

  const handleUpdateRecord = () => {
    loadTimeRecords();
  };

  const handleAddDay = async (data: {
    date: string;
    mark_entry?: string;
    lunch_entry?: string;
    lunch_exit?: string;
    final_exit?: string;
  }) => {
    await workerService.createManualTimeRecord(worker.id, data);
    loadTimeRecords();
  };

  const handleAddBonus = async (data: {
    week_start_date: string;
    amount: number;
    description?: string;
  }) => {
    await workerService.addWeeklyBonus(worker.id, data);
    loadTimeRecords();
  };

  const handleDeleteRecord = async (record: TimeRecord) => {
    if (!record.id || record.is_summary) return;
    if (!window.confirm('¿Eliminar este registro de día?')) return;
    try {
      await workerService.deleteTimeRecord(record.id);
      loadTimeRecords();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error al eliminar');
    }
  };

  const formatTime = (time: string | null | undefined) => {
    if (!time || time === '12:00 AM' || time === '00:00:00') return '-';
    
    try {
      // Intentar parsear como HH:mm:ss
      const parsed = dayjs(time, 'HH:mm:ss');
      if (parsed.isValid()) {
        return parsed.format('h:mm A');
      }
      
      // Si no funciona, intentar parsear como HH:mm
      const parsed2 = dayjs(time, 'HH:mm');
      if (parsed2.isValid()) {
        return parsed2.format('h:mm A');
      }
      
      // Si nada funciona, devolver el valor original
      return time;
    } catch (error) {
      console.error('Error parsing time:', time, error);
      return '-';
    }
  };

  const formatDate = (date: string) => {
    return dayjs(date).format('DD MMMM YYYY');
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatHours = (hours: number | null | undefined) => {
    if (hours === null || hours === undefined || isNaN(hours) || hours < 0) return '0 hr';
    return `${Number(hours).toFixed(2)} hr`;
  };

  const isWeeklySummary = (record: TimeRecord) => {
    return record.is_summary === true || record.date?.includes('Resumen Semanal');
  };

  const getDayNumber = (dayName: string): number => {
    const dayMap: { [key: string]: number } = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6
    };
    return dayMap[dayName] || 1; // Default to Monday if not found
  };

  const getDayNameInSpanish = (dayName: string): string => {
    const dayMap: { [key: string]: string } = {
      'Sunday': 'Domingo',
      'Monday': 'Lunes',
      'Tuesday': 'Martes',
      'Wednesday': 'Miércoles',
      'Thursday': 'Jueves',
      'Friday': 'Viernes',
      'Saturday': 'Sábado'
    };
    return dayMap[dayName] || 'Lunes';
  };

  const getWeeklySummaries = () => {
    if (timeRecords.length === 0) return [];
    
    // Separar registros reales de resúmenes del backend
    const backendSummaries = timeRecords.filter(record => isWeeklySummary(record));
    const realRecords = timeRecords.filter(record => !isWeeklySummary(record));
    
    // Obtener el primer día de la semana desde la configuración de la empresa
    const firstDayOfWeek = company ? getDayNumber(company.first_day_of_week) : 1; // Default to Monday
    
    // Ordenar registros por fecha (más recientes primero)
    const sortedRecords = realRecords.sort((a, b) => 
      dayjs(b.date).isBefore(dayjs(a.date)) ? -1 : 1
    );
    
    // Agrupar registros por semana usando el FIN de la semana como clave
    const weeklyGroups: { [key: string]: TimeRecord[] } = {};
    
    sortedRecords.forEach((record) => {
      const recordDate = dayjs(record.date);
      const dayOfWeek = recordDate.day();
      
      // Calcular el FIN de la semana (día anterior al primer día de la semana)
      const weekEndDay = (firstDayOfWeek - 1 + 7) % 7; // Día anterior al primer día
      
      // Calcular cuántos días restan hasta el fin de semana
      let daysToWeekEnd = (weekEndDay - dayOfWeek + 7) % 7;
      // Si es el mismo día, ya es el fin de la semana (no desplazar)
      
      const weekEnd = recordDate.add(daysToWeekEnd, 'day');
      const weekKey = weekEnd.format('YYYY-MM-DD');
      
      if (!weeklyGroups[weekKey]) {
        weeklyGroups[weekKey] = [];
      }
      weeklyGroups[weekKey].push(record);
    });
    
    // Crear el array final con divisiones semanales
    const result: any[] = [];
    const sortedWeeks = Object.keys(weeklyGroups).sort().reverse(); // Más recientes primero
    
    sortedWeeks.forEach((weekKey, weekIndex) => {
      const weekRecords = weeklyGroups[weekKey].sort((a, b) => 
        dayjs(b.date).isBefore(dayjs(a.date)) ? -1 : 1 // Más recientes primero dentro de la semana
      );
      
      // Agregar registros de la semana PRIMERO
      weekRecords.forEach(record => {
        result.push({
          type: 'record',
          data: record
        });
      });
      
      // Buscar resumen del backend para esta semana o calcularlo
      // Calcular weekStart usando la misma lógica que el backend
      const firstDayOfWeekNum = firstDayOfWeek;
      const weekStartDate = dayjs(weekKey).subtract(6, 'day');
      let weekStart = weekStartDate.format('YYYY-MM-DD');
      
      // Ajustar weekStart para que coincida exactamente con el cálculo del backend
      // El backend calcula el inicio de semana restando días hasta llegar al primer día configurado
      const weekStartMoment = dayjs(weekStart);
      let adjustedWeekStart = weekStartMoment;
      while (adjustedWeekStart.day() !== firstDayOfWeekNum) {
        adjustedWeekStart = adjustedWeekStart.subtract(1, 'day');
      }
      weekStart = adjustedWeekStart.format('YYYY-MM-DD');
      
      const backendSummary = backendSummaries.find(summary => {
        // El formato del backend es: "Resumen Semanal (YYYY-MM-DD - YYYY-MM-DD)"
        // Extraer la primera fecha (weekStart) del resumen
        const dateMatch = summary.date.match(/\((\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})\)/);
        if (dateMatch && dateMatch[1]) {
          return dateMatch[1] === weekStart;
        }
        // Fallback: buscar cualquier fecha que coincida con weekStart
        const summaryDate = summary.date.match(/\d{4}-\d{2}-\d{2}/)?.[0];
        return summaryDate === weekStart;
      });
      
      // Si encontramos el resumen del backend, usarlo directamente (ya incluye el bonus)
      // Si no, calcularlo localmente (pero sin bonus, ya que no tenemos esa info)
      const weekSummary = backendSummary || calculateWeekSummary(weekRecords, weekKey);
      
      // Si usamos el resumen del backend, asegurarnos de que tenga todos los campos necesarios
      if (backendSummary) {
        // El resumen del backend ya tiene bonus y total_pay calculados correctamente
        console.log(`✅ Usando resumen del backend para semana ${weekStart}:`, {
          bonus: backendSummary.bonus,
          total_pay: backendSummary.total_pay,
          date: backendSummary.date
        });
      } else if (backendSummaries.length > 0) {
        console.warn(`⚠️ No se encontró resumen del backend para semana ${weekStart}. Resúmenes disponibles:`, 
          backendSummaries.map(s => {
            const match = s.date.match(/\((\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})\)/);
            return { date: s.date, weekStart: match?.[1], bonus: s.bonus, total_pay: s.total_pay };
          })
        );
      }
      
      // Agregar resumen semanal DESPUÉS de todos los registros de la semana
      result.push({
        type: 'summary',
        data: weekSummary,
        weekKey: weekKey
      });
    });
    
    return result;
  };

  const calculateWeekSummary = (weekRecords: TimeRecord[], weekKey: string) => {
    // Calcular totales localmente (fallback si no hay resumen del backend)
    const totalNormalHours = weekRecords.reduce((sum, record) => {
      const hours = parseFloat(record.normal_hours?.toString() || '0');
      return sum + (isNaN(hours) ? 0 : hours);
    }, 0);
    
    const totalOvertimeHours = weekRecords.reduce((sum, record) => {
      const hours = parseFloat(record.overtime_hours?.toString() || '0');
      return sum + (isNaN(hours) ? 0 : hours);
    }, 0);
    
    const totalNormalPay = weekRecords.reduce((sum, record) => {
      const pay = parseFloat(record.normal_pay?.toString() || '0');
      return sum + (isNaN(pay) ? 0 : pay);
    }, 0);
    
    const totalOvertimePay = weekRecords.reduce((sum, record) => {
      const pay = parseFloat(record.overtime_pay?.toString() || '0');
      return sum + (isNaN(pay) ? 0 : pay);
    }, 0);
    
    const weekEnd = dayjs(weekKey);
    const weekStart = weekEnd.subtract(6, 'day');
    
    return {
      date: `Resumen Semanal (${weekStart.format('DD/MM')} - ${weekEnd.format('DD/MM/YYYY')})`,
      normal_hours: totalNormalHours,
      overtime_hours: totalOvertimeHours,
      normal_pay: totalNormalPay,
      overtime_pay: totalOvertimePay,
      bonus: 0,
      total_pay: totalNormalPay + totalOvertimePay,
      isWeeklySummary: true
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Cargando registros de tiempo...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Card */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              {worker.name}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
              {worker.company_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={`Primer día: ${company ? getDayNameInSpanish(company.first_day_of_week) : 'Lunes'}`} 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip 
                label={`Precio hora: ${formatCurrency(worker.price_per_hour)}`} 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip 
                label={`Overtime: ${formatCurrency(worker.overtime_price)}`} 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {(userRole === 'admin' || userRole === 'empresa' || userRole === 'super_admin') && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDayDialogOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  Agregar Día
                </Button>
                <Button
                  variant="contained"
                  startIcon={<MoneyIcon />}
                  onClick={() => setAddBonusDialogOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  Agregar Bono
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ListIcon />}
                  onClick={() => setManageBonusesDialogOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  Gestionar Bonos
                </Button>
              </>
            )}
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: 'white' }}>Vista</InputLabel>
              <Select
                value={viewType}
                label="Vista"
                onChange={(e) => setViewType(e.target.value as 'daily' | 'weekly')}
                sx={{ 
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.5)'
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white'
                  }
                }}
              >
                <MenuItem value="daily">Diario</MenuItem>
                <MenuItem value="weekly">Semanal</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '& .MuiTableCell-root': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.95rem'
                }
              }}>
                <TableCell>Día</TableCell>
                <TableCell colSpan={4} align="center">Horarios de Entrada</TableCell>
                <TableCell colSpan={3} align="center">Horas & Pagos</TableCell>
                {(userRole === 'admin' || userRole === 'empresa' || userRole === 'super_admin') && <TableCell align="center">Acciones</TableCell>}
              </TableRow>
              <TableRow sx={{ 
                backgroundColor: '#f8f9fa',
                '& .MuiTableCell-root': {
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  color: '#495057'
                }
              }}>
                <TableCell></TableCell>
                <TableCell>Entrada</TableCell>
                <TableCell>Inicio Almuerzo</TableCell>
                <TableCell>Fin Almuerzo</TableCell>
                <TableCell>Salida Final</TableCell>
                <TableCell>Normal / Pago</TableCell>
                <TableCell>Overtime / Pago</TableCell>
                <TableCell>Bono</TableCell>
                {(userRole === 'admin' || userRole === 'empresa' || userRole === 'super_admin') && <TableCell align="center">Editar</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {viewType === 'daily' ? (
                getWeeklySummaries().map((item, index) => {
                  const record = item.data;
                  const isSummary = item.type === 'summary';
                  
                  return (
                    <TableRow 
                      key={index} 
                      hover
                      sx={{
                        ...(isSummary ? { 
                          backgroundColor: '#e8f5e8',
                          borderTop: '3px solid #28a745',
                          '& .MuiTableCell-root': {
                            fontWeight: 'bold',
                            borderBottom: '2px solid #28a745'
                          }
                        } : {
                          '&:nth-of-type(odd)': {
                            backgroundColor: '#fafafa'
                          },
                          '&:hover': {
                            backgroundColor: '#f0f8ff',
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        })
                      }}
                    >
                      <TableCell sx={{ fontWeight: '600', color: '#495057' }}>
                        {isSummary ? (
                          <Chip 
                            label={`📊 ${record.date}`} 
                            color="success" 
                            size="small" 
                            sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}
                          />
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: '600' }}>
                              {formatDate(record.date)}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    <TableCell>
                      {isSummary ? (
                        <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                          -
                        </Typography>
                      ) : (
                        <Chip 
                          label={formatTime(record.mark_entry)} 
                          size="small" 
                          variant="outlined"
                          sx={{ minWidth: '80px' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isSummary ? (
                        <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                          -
                        </Typography>
                      ) : (
                        <Chip 
                          label={formatTime(record.lunch_entry)} 
                          size="small" 
                          variant="outlined"
                          sx={{ minWidth: '80px' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isSummary ? (
                        <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                          -
                        </Typography>
                      ) : (
                        <Chip 
                          label={formatTime(record.lunch_exit)} 
                          size="small" 
                          variant="outlined"
                          sx={{ minWidth: '80px' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isSummary ? (
                        <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                          -
                        </Typography>
                      ) : (
                        <Chip 
                          label={formatTime(record.final_exit)} 
                          size="small" 
                          variant="outlined"
                          sx={{ minWidth: '80px' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: '600', color: isSummary ? '#1976d2' : '#28a745' }}>
                          {formatHours(record.normal_hours)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6c757d' }}>
                          {formatCurrency(record.normal_pay)}
                        </Typography>
                        {isSummary && (
                          <>
                            {record.bonus && record.bonus > 0 && (
                              <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                                Bono: {formatCurrency(record.bonus)}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                              Total: {formatCurrency(record.total_pay)}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: '600', color: isSummary ? '#1976d2' : '#dc3545' }}>
                          {formatHours(record.overtime_hours)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6c757d' }}>
                          {formatCurrency(record.overtime_pay)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isSummary ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {record.bonus && record.bonus > 0 ? (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: '600', color: '#ff9800' }}>
                                {formatCurrency(record.bonus)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                Total: {formatCurrency(record.total_pay)}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                              0
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        record.bonus && record.bonus > 0 ? (
                          <Typography variant="body2" sx={{ fontWeight: '600', color: '#ff9800' }}>
                            {formatCurrency(record.bonus)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )
                      )}
                    </TableCell>
                    {(userRole === 'admin' || userRole === 'empresa' || userRole === 'super_admin') && (
                      <TableCell align="center">
                        {!isSummary && (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Editar horas">
                              <IconButton
                                size="small"
                                onClick={() => handleEditRecord(record)}
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  '&:hover': { backgroundColor: '#bbdefb', transform: 'scale(1.1)' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar día">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRecord(record)}
                                sx={{
                                  backgroundColor: '#ffebee',
                                  color: '#c62828',
                                  '&:hover': { backgroundColor: '#ffcdd2', transform: 'scale(1.1)' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })
              ) : (
                getWeeklySummaries().map((week, weekIndex) => (
                  <React.Fragment key={weekIndex}>
                    {week.data.map((record: TimeRecord, dayIndex: number) => (
                      <TableRow 
                        key={`${weekIndex}-${dayIndex}`} 
                        hover
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: '#fafafa'
                          },
                          '&:hover': {
                            backgroundColor: '#f0f8ff',
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: '600', color: '#495057' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: '600' }}>
                              {formatDate(record.date)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatTime(record.mark_entry)} 
                            size="small" 
                            variant="outlined"
                            sx={{ minWidth: '80px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatTime(record.lunch_entry)} 
                            size="small" 
                            variant="outlined"
                            sx={{ minWidth: '80px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatTime(record.lunch_exit)} 
                            size="small" 
                            variant="outlined"
                            sx={{ minWidth: '80px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatTime(record.final_exit)} 
                            size="small" 
                            variant="outlined"
                            sx={{ minWidth: '80px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: '600', color: '#28a745' }}>
                              {formatHours(record.normal_hours)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                              {formatCurrency(record.normal_pay)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: '600', color: '#dc3545' }}>
                              {formatHours(record.overtime_hours)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                              {formatCurrency(record.overtime_pay)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {record.bonus && record.bonus > 0 ? (
                            <Typography variant="body2" sx={{ fontWeight: '600', color: '#ff9800' }}>
                              {formatCurrency(record.bonus)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        {(userRole === 'admin' || userRole === 'empresa' || userRole === 'super_admin') && (
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Editar horas">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditRecord(record)}
                                  sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    '&:hover': { backgroundColor: '#bbdefb', transform: 'scale(1.1)' }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar día">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteRecord(record)}
                                  sx={{
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    '&:hover': { backgroundColor: '#ffcdd2', transform: 'scale(1.1)' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {week.summary && (
                      <TableRow sx={{ 
                        backgroundColor: '#e8f5e8',
                        '& .MuiTableCell-root': {
                          fontWeight: 'bold'
                        }
                      }}>
                        <TableCell sx={{ fontWeight: '600', color: '#495057' }}>
                          <Chip 
                            label="📊 Resumen Semanal" 
                            color="success" 
                            size="small" 
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatTime(week.summary.mark_entry)} 
                            size="small" 
                            variant="outlined"
                            sx={{ minWidth: '80px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatTime(week.summary.lunch_entry)} 
                            size="small" 
                            variant="outlined"
                            sx={{ minWidth: '80px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatTime(week.summary.lunch_exit)} 
                            size="small" 
                            variant="outlined"
                            sx={{ minWidth: '80px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatTime(week.summary.final_exit)} 
                            size="small" 
                            variant="outlined"
                            sx={{ minWidth: '80px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: '600', color: '#28a745' }}>
                              {formatHours(week.summary.normal_hours)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                              {formatCurrency(week.summary.normal_pay)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: '600', color: '#dc3545' }}>
                              {formatHours(week.summary.overtime_hours)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                              {formatCurrency(week.summary.overtime_pay)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {(week.summary as any).bonus && (week.summary as any).bonus > 0 ? (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: '600', color: '#ff9800' }}>
                                  {formatCurrency((week.summary as any).bonus)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                  Total: {formatCurrency(week.summary.total_pay)}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                                -
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {(userRole === 'admin' || userRole === 'empresa' || userRole === 'super_admin') && <TableCell></TableCell>}
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Dialog de edición */}
        <EditTimeRecordDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          record={selectedRecord}
          onUpdate={handleUpdateRecord}
        />

        {/* Dialog para agregar día */}
        <AddDayDialog
          open={addDayDialogOpen}
          onClose={() => setAddDayDialogOpen(false)}
          onSave={handleAddDay}
          workerId={worker.id}
        />

        {/* Dialog para agregar bono */}
        <AddBonusDialog
          open={addBonusDialogOpen}
          onClose={() => setAddBonusDialogOpen(false)}
          onSave={handleAddBonus}
          workerId={worker.id}
          firstDayOfWeek={company?.first_day_of_week}
        />

        {/* Dialog para gestionar bonos (editar/eliminar) */}
        <ManageBonusesDialog
          open={manageBonusesDialogOpen}
          onClose={() => setManageBonusesDialogOpen(false)}
          workerId={worker.id}
          onUpdate={loadTimeRecords}
        />
      </Paper>
    </Box>
  );
};
